import { useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronRight, ChevronDown, MapPin, Heart, Newspaper, Home } from "lucide-react";
import { CITIES, COUNTRIES } from "@/cityConfig";

const citiesByCountry = Object.values(CITIES).reduce((acc, city) => {
  if (!acc[city.country]) acc[city.country] = [];
  acc[city.country].push(city);
  return acc;
}, {});

const T = {
  ru: { menu: "МЕНЮ", home: "Главная", blog: "Блог", weddings: "Свадебная флористика", cities: "ГОРОДА", soon: "Скоро открытие" },
  en: { menu: "MENU", home: "Home", blog: "Blog", weddings: "Wedding floristry", cities: "CITIES", soon: "Coming soon" },
  tr: { menu: "MENÜ", home: "Ana sayfa", blog: "Blog", weddings: "Düğün çiçekçiliği", cities: "ŞEHİRLER", soon: "Yakında" },
};

// Light-theme mobile menu (hamburger) for blog pages.
export default function BlogMobileMenu({ lang = "ru" }) {
  const [open, setOpen] = useState(false);
  const [activeCountry, setActiveCountry] = useState(null);
  const t = T[lang] || T.ru;
  const q = lang !== "ru" ? `?lang=${lang}` : "";

  const close = () => { setOpen(false); setActiveCountry(null); };
  const cityName = (c) => (lang === "tr" ? (c.nameTr || c.nameEn) : (lang === "en" ? c.nameEn : c.name));
  const countryName = (c) => (lang === "tr" ? (c.nameTr || c.nameEn) : (lang === "en" ? c.nameEn : c.name));

  return (
    <div className="sm:hidden">
      <button
        onClick={() => setOpen(true)}
        aria-label={t.menu}
        className="p-1.5 -mr-1 text-gray-700 hover:text-pink-600 transition-colors"
        data-testid="blog-mobile-menu-btn"
      >
        <Menu className="w-6 h-6" strokeWidth={1.75} />
      </button>

      {open && (
        <div className="fixed inset-0 z-50" data-testid="blog-mobile-menu">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={close} data-testid="blog-mobile-menu-backdrop" />
          <div className="absolute top-0 right-0 h-full w-[84%] max-w-[360px] bg-white shadow-2xl flex flex-col animate-fade-in overflow-y-auto overscroll-contain">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <span className="text-xs tracking-[0.2em] text-pink-600 font-bold">{t.menu}</span>
              <button onClick={close} aria-label="Close" className="p-1 text-gray-500 hover:text-gray-900 transition-colors" data-testid="blog-mobile-menu-close">
                <X className="w-6 h-6" strokeWidth={1.75} />
              </button>
            </div>

            <nav className="px-3 py-3 flex-1">
              <Link to={`/${q}`} onClick={close} className="flex items-center gap-3 py-3 px-3 rounded-xl text-gray-700 hover:bg-pink-50 hover:text-pink-600 font-semibold transition-colors" data-testid="blog-mobile-home">
                <Home className="w-4 h-4" /> {t.home}
              </Link>
              <Link to={`/blog${q}`} onClick={close} className="flex items-center gap-3 py-3 px-3 rounded-xl text-gray-700 hover:bg-pink-50 hover:text-pink-600 font-semibold transition-colors" data-testid="blog-mobile-blog">
                <Newspaper className="w-4 h-4" /> {t.blog}
              </Link>
              <Link to={`/weddings${q}`} onClick={close} className="flex items-center gap-2 py-3 px-3 my-1 rounded-xl font-semibold text-white bg-gradient-to-r from-pink-500 to-rose-500 shadow-md shadow-pink-100 transition-all" data-testid="blog-mobile-weddings">
                <Heart className="w-4 h-4 shrink-0" fill="currentColor" /> {t.weddings}
              </Link>

              <p className="px-3 pt-4 pb-1 text-[10px] tracking-[0.25em] text-gray-400 font-bold">{t.cities}</p>
              {Object.entries(COUNTRIES).map(([slug, country]) => {
                const cities = citiesByCountry[slug] || [];
                const isActive = activeCountry === slug;
                return (
                  <div key={slug}>
                    <button
                      onClick={() => setActiveCountry(isActive ? null : slug)}
                      className="w-full flex items-center justify-between py-2.5 px-3 rounded-xl text-sm font-semibold text-gray-700 hover:bg-pink-50 transition-colors"
                      data-testid={`blog-mobile-country-${slug}`}
                    >
                      <span className="flex items-center gap-2"><span>{country.flag}</span>{countryName(country)}</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${isActive ? "rotate-180" : ""}`} />
                    </button>
                    {isActive && cities.length > 0 && (
                      <div className="ml-3 border-l-2 border-pink-100 pl-2 mb-1">
                        {cities.map((city) => (
                          <Link
                            key={city.slug}
                            to={`/${city.slug}${q}`}
                            onClick={close}
                            className="flex items-center gap-2 py-2 px-3 rounded-lg text-sm text-gray-500 hover:bg-pink-50 hover:text-pink-600 transition-colors"
                            data-testid={`blog-mobile-city-${city.slug}`}
                          >
                            <MapPin className="w-3 h-3" /> {cityName(city)}
                          </Link>
                        ))}
                      </div>
                    )}
                    {isActive && cities.length === 0 && (
                      <div className="ml-3 pl-5 py-2 text-xs text-gray-400">{t.soon}</div>
                    )}
                  </div>
                );
              })}
            </nav>
          </div>
        </div>
      )}
    </div>
  );
}
